/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  STORAGE,
  getApocalypseConfig,
  decodeApocalypsePasscode,
  setApocalypseArmed,
  clearApocalypseArmed,
  apocalypseCardLabel,
  writeClipboardFx,
  unlockApocalypseAudio,
  playIphoneKeypadTap,
  setAcrosticCard,
  setSearchForceArmed,
  setSearchForceAnswers,
  setSearchForceFrozenDate,
  clearSearchForcePersisted,
  getSearchForceConfig,
  hapticTick,
  hapticSuccess,
  hapticError,
} from "@/lib/mr";

/**
 * Fake iOS lockscreen — the silent entry point for the Notes Apocalypse.
 *
 * Two-phase flow that matches the real iOS lock behaviour:
 *   1. Idle screen      → wallpaper + big clock + date + "swipe up to unlock"
 *      + flashlight / camera quick-action icons (cosmetic; long-press fizzles).
 *   2. Passcode screen  → minimised clock + "Enter Passcode" + dot indicators
 *      + iOS-style numeric keypad with rings around each digit.
 *
 * Magician swipes up, types the coded passcode (matches their real
 * passcode length thanks to Settings), and on the last digit the screen
 * "unlocks" — a quick fade routes to /notes with the Apocalypse armed.
 * If the code doesn't decode to a valid card, the dot row shakes like a
 * real wrong-passcode and resets, no other tell.
 *
 * Triple-tap on the very bottom of the screen exits to Setup so the
 * magician can get back to the control panel without ever performing.
 */
export default function LockScreen() {
  const navigate = useNavigate();
  const cfg = useRef(getApocalypseConfig()).current;
  const cardLength = cfg.length; // 4 or 6 — the number of card-passcode digits
  const [phase, setPhase] = useState("idle"); // "idle" | "passcode" | "unlocking"
  const [digits, setDigits] = useState("");
  const [shake, setShake] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // Which trick the current passcode entry will arm. Set to "apocalypse"
  // when the magician swipes up to unlock (default flow) and switched to
  // "swiper" when they tap the flashlight quick-action. Reset back to
  // "apocalypse" whenever we return to the idle wallpaper so the
  // spectator's next unlock is never accidentally hijacked.
  const [armMode, setArmMode] = useState("apocalypse");

  // --- Search-Force in-plain-sight arm sequence --------------------------
  // While armMode === "searchforce" the passcode entry happens in TWO
  // identical-looking rounds — indistinguishable from a spectator's
  // first-attempt-was-wrong iPhone unlock:
  //
  //   Round 1 ("answers phase"): the magician taps `cardLength` digits.
  //     The FIRST 4 taps encode the personality answers ("1" = cancel-
  //     side label, "2" = emergency-side label). Any extra taps beyond
  //     4 (only relevant for 6-digit passcodes) fill dots for visual
  //     parity but store nothing. When the dot row fills completely
  //     we mimic a WRONG PASSCODE: shake + clear dots. The stored
  //     sfAnswers survive so they can be re-used in Round 2.
  //
  //   Round 2 ("card phase"): dot row is empty again. The magician
  //     now types the real card passcode. Normal decode → arm →
  //     transition. If it doesn't match, same wrong-shake as always.
  //
  // Cancel + Erase behave like real iOS (idle / backspace). Erase
  // during answers phase also pops the last collected answer.
  const [sfAnswers, setSfAnswers] = useState([]);
  const [sfPhase, setSfPhase] = useState("answers"); // "answers" | "card"
  // Snapshot the eight configurable labels once per mount so we never
  // read localStorage inside a click handler (avoids stale reads
  // during rapid taps).
  const sfLabels = useRef({
    cancel:    ["morning", "cats", "tea", "introvert"],
    emergency: ["evening", "dogs", "coffee", "extrovert"],
  });
  useEffect(() => {
    try {
      const c = getSearchForceConfig();
      sfLabels.current = {
        cancel:    [c.q1CancelLabel,    c.q2CancelLabel,    c.q3CancelLabel,    c.q4CancelLabel],
        emergency: [c.q1EmergencyLabel, c.q2EmergencyLabel, c.q3EmergencyLabel, c.q4EmergencyLabel],
      };
    } catch { /* ignore — defaults stand */ }
  }, []);
  const isSearchForceArm = armMode === "searchforce";
  // Dot row length is ALWAYS the raw card-passcode length so the
  // spectator sees a normal iOS passcode entry (4 or 6 dots) with no
  // giveaway extra positions.
  const totalLength = cardLength;

  // Auto-grant: if Settings → Apocalypse has "Auto-request motion"
  // enabled (default), silently call DeviceMotionEvent.requestPermission()
  // on the very first user gesture this session. If iOS still remembers
  // a prior grant the call resolves invisibly — no prompt. If iOS
  // forgot, the popup appears at the natural moment the spectator is
  // unlocking and they just tap "Allow". The flag below ensures we only
  // ever ask ONCE per LockScreen mount so a hesitant spectator isn't
  // re-prompted after dismissing.
  const motionAskedRef = useRef(false);
  const askMotionOnce = useCallback(() => {
    if (motionAskedRef.current) return;
    motionAskedRef.current = true;
    // Audio unlock has to happen inside a user gesture on iOS — piggyback
    // on the first lockscreen tap regardless of the motion-permission
    // toggle, since the audio context can be unlocked for free even if
    // sounds are disabled in Settings (they just won't play).
    try { unlockApocalypseAudio(); } catch { /* ignore */ }
    if (!cfg.autoMotion) return;
    try {
      const E = typeof window !== "undefined" ? window.DeviceMotionEvent : null;
      if (E && typeof E.requestPermission === "function") {
        E.requestPermission().catch(() => { /* user denied — no-op */ });
      }
    } catch { /* very old iOS — no-op */ }
  }, [cfg.autoMotion]);

  // Live clock — updates every 10s, which is the same cadence iOS uses
  // for the lockscreen time and is plenty smooth for a fake.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(id);
  }, []);

  // Auto-submit when the dot row is full. For Search-Force mode the
  // FIRST 4 digits are the personality-answer prefix (silently pushed
  // into sfAnswers by `press` below); the card passcode occupies the
  // remaining `cardLength` positions. Decoding is synchronous: if
  // the digits map to a valid card, arm + transition; otherwise
  // wrong-shake and clear. Clipboard FX (if enabled) is written HERE
  // because this useEffect chain is still inside the keypad's user-
  // gesture activation, and iOS Safari refuses navigator.clipboard
  // writes outside that window.
  useEffect(() => {
    if (digits.length < totalLength) return;
    // Search-Force "answers round": the FIRST full dot row is a decoy.
    // Silently keep sfAnswers, wrong-shake, clear digits, and flip to
    // the card phase so the NEXT full row is the real passcode entry.
    if (isSearchForceArm && sfPhase === "answers") {
      setShake(true);
      hapticError();
      const id = setTimeout(() => {
        setShake(false);
        setDigits("");
        setSfPhase("card");
      }, 600);
      return () => clearTimeout(id);
    }
    const card = decodeApocalypsePasscode(digits);
    if (!card) {
      setShake(true);
      hapticError();
      const id = setTimeout(() => {
        setShake(false);
        setDigits("");
        // A wrong CARD attempt drops the magician back to the answer
        // round so they can try the whole in-plain-sight arm again.
        if (isSearchForceArm) {
          setSfPhase("answers");
          setSfAnswers([]);
        }
      }, 600);
      return () => clearTimeout(id);
    }
    setApocalypseArmed(card, cfg.delay);
    // Alt-arming path — if the magician entered this passcode via the
    // flashlight quick-action, redirect the card to the Acrostic-Notes
    // trick via ACROSTIC_CARD storage. In this mode we intentionally
    // SKIP setApocalypseArmed above by clearing it back out, so the
    // decoy list opens in its normal state and only the acrostic
    // rewrite is primed. Done this way (arm-then-clear) to keep the
    // writeClipboardFx path below unchanged for both modes.
    if (armMode === "acrostic") {
      try {
        // Undo the arm we just did — acrostic mode should not fire the
        // apocalypse chain when the spectator opens the trigger note.
        clearApocalypseArmed();
      } catch { /* ignore */ }
      try { setAcrosticCard({ value: card.value, suit: card.suit }); } catch { /* ignore */ }
    }
    // Alt-arming path #2 — camera quick-action arms the Search-Force
    // trick. Same idea as swiper: undo the accidental Apocalypse arm
    // above, then stash the card label + arm timestamp so the fake
    // prediction note that surfaces from the search bar reads with a
    // LIVE "just now" date line and the (card) placeholder in the
    // template substituted with the card the magician just entered.
    if (armMode === "searchforce") {
      try { clearApocalypseArmed(); } catch { /* ignore */ }
      try {
        // Store the raw {value, suit} so DecoyNotes can localize the
        // label via apocalypseCardLabel(card, langCode) at render time.
        setSearchForceArmed({ value: card.value, suit: card.suit });
        // Persist the 4 personality answers alongside the card so the
        // notes page can weave them into the (q1)/(q2)/(q3)/(q4)
        // template placeholders. Only meaningful when the magician
        // actually completed the 4-question sequence; partial arms
        // still store whatever was collected (unused placeholders
        // collapse to "").
        setSearchForceAnswers(sfAnswers);
        // Freeze the (date) placeholder timestamp at this exact
        // moment (minus the configured offset). Prevents the note
        // from silently updating its clock if the spectator taps it
        // hours later.
        try {
          const cfgSF = getSearchForceConfig();
          setSearchForceFrozenDate(cfgSF.dateOffsetMinutes || 0);
        } catch { /* live-now fallback in DecoyNotes still works */ }
        // Wipe any leftover session-persisted prediction from a
        // previous arm so we always start with a clean slate.
        try { clearSearchForcePersisted(); } catch { /* ignore */ }
      } catch { /* ignore */ }
    }
    // FX #3 — Clipboard hijack. Write the configured card text now,
    // while we're still inside the keypad-tap activation. After the
    // trick wraps the spectator can paste anywhere (Notes, Messages,
    // Safari URL bar) and the card prediction appears.
    if (cfg.fxClipboardEnabled) {
      const label = apocalypseCardLabel(card, "en");
      const text = (cfg.fxClipboardTemplate || "").replace(/\(xxx\)/gi, label).replace(/\{card\}/gi, label);
      if (text) writeClipboardFx(text);
    }
    setPhase("unlocking");
    hapticSuccess();
    const id = setTimeout(() => {
      navigate("/notes", { replace: true });
    }, 650);
    return () => clearTimeout(id);
  }, [digits, totalLength, isSearchForceArm, sfPhase, cfg.delay, cfg.fxClipboardEnabled, cfg.fxClipboardTemplate, navigate, armMode, sfAnswers]);

  const press = useCallback(
    (d) => {
      if (phase === "unlocking") return;
      // Play the real iOS keypad tap sample — digits only. Backspace
      // and Cancel remain silent to match real iOS behaviour.
      playIphoneKeypadTap({ volume: 0.55 });
      // Realism: fire a tiny haptic tick on every digit press so the
      // phone feels native under the thumb. Matches iOS keypad feel.
      hapticTick();
      // Search-Force ANSWERS ROUND: dot row fills like a normal
      // passcode entry but the FIRST 4 taps double as personality
      // answers. Digit "1" pushes the cancel-side label for the
      // current question index, digit "2" pushes the emergency-side
      // label. Other digits fill a dot but store an empty answer.
      // Taps beyond position 3 (only reachable when cardLength ≥ 5)
      // fill dots for visual parity but store nothing — magicians
      // should stick to 4 answer taps regardless.
      if (isSearchForceArm && sfPhase === "answers" && phase === "passcode") {
        const idx = digits.length;
        if (idx < 4) {
          let label = "";
          if (d === "1") label = sfLabels.current.cancel[idx]    || "";
          else if (d === "2") label = sfLabels.current.emergency[idx] || "";
          setSfAnswers((prev) => [...prev, label]);
        }
        setDigits((prev) => (prev.length >= totalLength ? prev : prev + d));
        return;
      }
      setDigits((prev) => (prev.length >= totalLength ? prev : prev + d));
    },
    [totalLength, phase, isSearchForceArm, sfPhase, digits.length],
  );

  const back = useCallback(() => {
    // React StrictMode double-invokes state-updater callbacks to catch
    // impure updaters, so we CANNOT nest setSfAnswers inside setDigits'
    // updater — it would pop twice. Read digits.length from the closure
    // (kept fresh by the useCallback dep) and dispatch each setState
    // exactly once so both stay in lock-step even in dev builds.
    if (digits.length === 0) return;
    setDigits((prev) => (prev.length === 0 ? prev : prev.slice(0, -1)));
    if (isSearchForceArm && sfPhase === "answers" && digits.length <= 4) {
      setSfAnswers((a) => a.slice(0, -1));
    }
  }, [digits.length, isSearchForceArm, sfPhase]);

  // Cancel — return to the idle wallpaper, matching real iOS behaviour
  // where the "Cancel" button at the bottom-right of the passcode pane
  // collapses you back to the locked wallpaper. Clears any partial
  // digits so the next attempt starts clean.
  const cancelToIdle = useCallback(() => {
    setDigits("");
    setShake(false);
    setPhase("idle");
    setArmMode("apocalypse");
    setSfAnswers([]);
    setSfPhase("answers");
  }, []);

  // --- Idle gestures → reveal the keypad ----------------------------------
  // Real iOS swipe-up gesture: the lock UI follows the finger upward
  // while dragging, and snaps back if released without enough travel.
  // Crossing the threshold (≥80px upward) reveals the passcode panel —
  // matching native Face ID behaviour. A plain tap does NOT advance,
  // since the user explicitly requested a "real swipe" gesture.
  const SWIPE_THRESHOLD_PX = 80;
  const SWIPE_MAX_PX = 360;
  const dragStart = useRef({ y: 0, t: 0, active: false, hapticFired: false });
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const onIdlePointerDown = (e) => {
    dragStart.current = { y: e.clientY, t: Date.now(), active: true, hapticFired: false };
    setDragging(true);
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
  };
  const onIdlePointerMove = (e) => {
    if (!dragStart.current.active) return;
    const dy = e.clientY - dragStart.current.y;
    // Only respond to upward motion; clamp downward to 0 so the UI
    // can't be pulled below its resting position.
    const up = Math.max(0, Math.min(SWIPE_MAX_PX, -dy));
    setDragY(up);
    // Silent convincer: fire a tiny haptic tick the moment the unlock
    // threshold is crossed during the drag. Matches the feel of native
    // iOS Face ID unlock confirmation. Only fires ONCE per gesture
    // (hapticFired flag) so the spectator's wrist doesn't buzz on
    // every pointermove past the threshold. Falls back silently on
    // hardware without the Vibration API (most iOS) — no tell.
    if (!dragStart.current.hapticFired && up >= SWIPE_THRESHOLD_PX) {
      dragStart.current.hapticFired = true;
      hapticTick();
    }
  };
  const onIdlePointerUp = (e) => {
    if (!dragStart.current.active) return;
    const dy = e.clientY - dragStart.current.y;
    dragStart.current.active = false;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
    const isSwipeUp = -dy >= SWIPE_THRESHOLD_PX;
    if (isSwipeUp) {
      // Advance to the passcode phase. The phase change unmounts the
      // idle layer, so the dragY value no longer matters; we reset it
      // to 0 so any future return to idle (via Cancel) starts at rest.
      setPhase("passcode");
      setDragY(0);
    } else {
      // Spring back to rest.
      setDragY(0);
    }
  };
  const onIdlePointerCancel = () => {
    if (!dragStart.current.active) return;
    dragStart.current.active = false;
    setDragging(false);
    setDragY(0);
  };

  // Triple-tap the very bottom "home indicator" to bail back to Setup.
  // Gives the magician a way to exit without performing the trick.
  // We track on pointerdown (NOT click) because the parent container
  // captures the pointer on its own pointerdown for the swipe gesture,
  // which would otherwise swallow the click event on this button.
  // stopPropagation on pointerdown prevents the parent's swipe handler
  // from kicking in while the magician is triple-tapping the indicator.
  const exitTaps = useRef([]);
  const onHomeIndicatorPointerDown = (e) => {
    e.stopPropagation();
    const t = Date.now();
    exitTaps.current = [...exitTaps.current, t].filter((x) => t - x < 800);
    if (exitTaps.current.length >= 3) {
      exitTaps.current = [];
      navigate("/", { replace: true });
    }
  };

  // Date stamp formatted like a real iOS lockscreen: SHORT weekday +
  // day + SHORT month, capitalised. For nl-NL that gives "ma 6 jul"
  // (which we then upper-case the initials of to "Ma 6 Jul" — the
  // way native iOS renders it). For en it gives "Mon Jul 6". iOS uses
  // this compact form since iOS 16+; the previous long form ("Monday,
  // April 28") was inaccurate.
  const dateStr = useMemo(() => {
    const raw = now.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    // Some locales (nl, de, fr, …) emit lowercase weekday/month names.
    // iOS capitalises the first letter of every word — replicate.
    return raw
      .replace(/[a-zà-ÿ]+/gi, (word) =>
        word.charAt(0).toLocaleUpperCase() + word.slice(1)
      )
      // Strip trailing periods some locales add after abbreviations
      // (e.g. Norwegian "man.") — iOS omits them.
      .replace(/\./g, "");
  }, [now]);
  const timeStr = useMemo(
    () =>
      now.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    [now]
  );

  // Status bar values — keep them static and "iPhone-y". The signal /
  // wifi / battery icons are SVG so they render identically across
  // devices and don't tell on us.
  const wallpaperStyle = cfg.wallpaper
    ? { backgroundImage: `url(${cfg.wallpaper})`, backgroundSize: "cover", backgroundPosition: "center" }
    : {
        background:
          "radial-gradient(120% 80% at 50% 10%, #2a2f3a 0%, #11141a 55%, #05070a 100%)",
      };

  const dotsFilled = digits.length;
  const unlocking = phase === "unlocking";

  return (
    <div
      className="fixed inset-0 w-screen h-screen overflow-hidden select-none text-white"
      style={{
        ...wallpaperStyle,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        touchAction: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDownCapture={askMotionOnce}
      onPointerDown={phase === "idle" ? onIdlePointerDown : undefined}
      onPointerMove={phase === "idle" ? onIdlePointerMove : undefined}
      onPointerUp={phase === "idle" ? onIdlePointerUp : undefined}
      onPointerCancel={phase === "idle" ? onIdlePointerCancel : undefined}
      data-testid="lockscreen-page"
    >
      {/* Dim layer over wallpaper for legibility (iOS does this too) */}
      <div className="absolute inset-0 bg-black/25 pointer-events-none" />

      {/* Unlock fade — fade the lock UI (clock, dots, keypad) only, NEVER
          the wallpaper. iOS keeps the wallpaper rock-steady from lock
          through home/Notes; flashing it to black + then to white was
          the giveaway. We now hand the wallpaper off invisibly to the
          DecoyNotes mount, which renders the same image as a fading
          backdrop on top of itself. */}

      {/* No fake status bar — the real device status bar above already
          shows the real time / wifi / battery, so duplicating them here
          would be an obvious tell (two clocks, two battery icons). */}

      {/* PHASE: IDLE — clock + date + lock + swipe hint + quick actions.
          The whole idle layer rides up under the finger during a swipe
          (translateY follows dragY 1:1) and springs back when the user
          releases before the threshold. Matches native iOS Face ID
          unlock motion. */}
      {phase === "idle" && (
        <div
          className="absolute inset-0 flex flex-col items-center pt-[22%] pointer-events-none"
          style={{
            opacity: unlocking ? 0 : Math.max(0, 1 - dragY / SWIPE_MAX_PX),
            transform: `translateY(${-dragY}px)`,
            transition: dragging
              ? "none"
              : "transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms ease",
            willChange: "transform, opacity",
          }}
        >
          <LockGlyph />
          <div
            className="mt-3 text-[17px] font-semibold tracking-[0.01em]"
            style={{
              // iOS lockscreen date: SF Pro Rounded semibold, white with a
              // whisper-soft shadow for readability on light wallpapers.
              fontFamily:
                '"SF Pro Rounded", ui-rounded, "SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
              color: "rgba(255,255,255,0.95)",
              textShadow: "0 1px 3px rgba(0,0,0,0.22)",
            }}
          >
            {dateStr}
          </div>
          <div
            className="mt-1 leading-none tabular-nums"
            style={{
              // iOS 18 lockscreen clock: uses SF Pro Display (NOT rounded) as
              // the default face. The digits are tall, straight-shouldered
              // and lightly-curved — noticeably less circular than SF Pro
              // Rounded. Keep the frosted-glass fill so the wallpaper
              // reads through the characters.
              fontFamily:
                '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", system-ui, sans-serif',
              fontSize: "116px",
              fontWeight: 800,
              letterSpacing: "-0.055em",
              color: "rgba(255,255,255,0.38)",
              textShadow: [
                "0 -1px 0 rgba(255,255,255,0.28)",  // top rim highlight
                "0 2px 3px rgba(0,0,0,0.14)",       // bottom shadow (depth)
                "0 0 24px rgba(255,255,255,0.10)",  // frosted aura
              ].join(", "),
            }}
            data-testid="lockscreen-clock"
          >
            {timeStr}
          </div>

          {/* Quick action buttons (flashlight + camera). Both are silent
              alt-arm entry points for the magician:
                • Flashlight → armMode "acrostic" → card written to
                  ACROSTIC_CARD; on /notes the middle N titles are
                  quietly rewritten so first-letters spell the card
                • Camera     → armMode "searchforce" → card armed for the
                  Search-Force prediction note (LIVE date + (card) sub)
              `pointer-events-auto` is scoped inside each QuickAction so
              the wallpaper-wide swipe-up gesture is unaffected. */}
          <div className="absolute bottom-[10%] inset-x-0 px-10 flex items-center justify-between pointer-events-none">
            <QuickAction
              kind="flashlight"
              onTap={() => {
                setArmMode("acrostic");
                setDigits("");
                setShake(false);
                setPhase("passcode");
              }}
            />
            <QuickAction
              kind="camera"
              onTap={() => {
                setArmMode("searchforce");
                setDigits("");
                setShake(false);
                setSfAnswers([]);
                setSfPhase("answers");
                setPhase("passcode");
              }}
            />
          </div>
        </div>
      )}

      {/* PHASE: PASSCODE — lock glyph + prompt + dots + keypad
          (clock + date intentionally hidden once the keypad appears,
          matching native iOS where the big time/date collapse away to
          keep the spectator's eye on the passcode input) */}
      {phase !== "idle" && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-500"
          style={{ opacity: unlocking ? 0 : 1 }}
        >
          <LockGlyph small />

          <div className="mt-5 text-[17px] font-semibold">Enter Passcode</div>
          <DotRow length={totalLength} filled={dotsFilled} shake={shake} />

          <Keypad
            onDigit={press}
            onBack={back}
            canBack={digits.length > 0}
          />

          {/* Emergency (left) + Cancel (right) — real iOS lockscreen
              footer. Emergency is purely cosmetic (no SOS call, ever).
              Cancel returns to the idle wallpaper. Both buttons no
              longer carry secret arming semantics — that role moved
              to digits "1" and "2" on the keypad above, so the entire
              arm sequence now happens in plain sight as what looks
              like a normal (albeit slightly long) passcode entry. */}
          <div
            className="absolute inset-x-0 flex items-center justify-between px-10 text-white text-[17px]"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 56px)" }}
          >
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); }}
              className="bg-transparent border-0 p-0 text-white opacity-95 active:opacity-60 transition-opacity"
              data-testid="lockscreen-emergency"
              aria-label="Emergency"
            >
              Emergency
            </button>
            <button
              type="button"
              onClick={cancelToIdle}
              className="bg-transparent border-0 p-0 text-white opacity-95 active:opacity-60 transition-opacity"
              data-testid="lockscreen-cancel"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* iOS home indicator at the very bottom — also our quiet exit
          hotspot. Triple-tap to bail back to Setup. */}
      <button
        type="button"
        className="absolute bottom-2 left-1/2 -translate-x-1/2 h-1.5 w-[120px] rounded-full bg-white/85 border-0 p-0"
        aria-label="home"
        onPointerDown={onHomeIndicatorPointerDown}
        data-testid="lockscreen-home-indicator"
      />

      {/* Shake keyframes (used by DotRow) */}
      <style>{`
        @keyframes mr-shake {
          0%,100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
      `}</style>
    </div>
  );
}

/* -----------------------------------------------------------------------
 *                            Building blocks
 * --------------------------------------------------------------------- */

function LockGlyph({ small = false }) {
  const w = small ? 18 : 22;
  const h = small ? 22 : 26;
  return (
    <svg width={w} height={h} viewBox="0 0 22 26" aria-hidden="true">
      <rect x="3"  y="11" width="16" height="13" rx="3" fill="white" />
      <path
        d="M6 11V8a5 5 0 0 1 10 0v3"
        stroke="white"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="11" cy="17" r="1.7" fill="rgba(0,0,0,0.4)" />
    </svg>
  );
}

function DotRow({ length, filled, shake }) {
  const items = Array.from({ length });
  return (
    <div
      className="mt-6 flex items-center gap-5"
      style={shake ? { animation: "mr-shake 0.55s ease" } : undefined}
      data-testid="lockscreen-dots"
    >
      {items.map((_, i) => {
        const on = i < filled;
        return (
          <div
            key={i}
            className="w-[14px] h-[14px] rounded-full transition-colors"
            style={{
              border: "1.5px solid rgba(255,255,255,0.85)",
              background: on ? "rgba(255,255,255,0.95)" : "transparent",
            }}
            data-testid={`lockscreen-dot-${i}`}
            data-filled={on ? "true" : "false"}
          />
        );
      })}
    </div>
  );
}

const LETTERS = {
  1: "",
  2: "ABC",
  3: "DEF",
  4: "GHI",
  5: "JKL",
  6: "MNO",
  7: "PQRS",
  8: "TUV",
  9: "WXYZ",
};

function Keypad({ onDigit, onBack, canBack }) {
  const rows = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["", "0", "back"],
  ];
  return (
    <div className="mt-10 grid grid-cols-3 gap-x-7 gap-y-4 px-4">
      {rows.flat().map((d, i) => {
        if (d === "") return <div key={i} />;
        if (d === "back") {
          return (
            <button
              key={i}
              type="button"
              onClick={onBack}
              className="w-[76px] h-[76px] flex items-center justify-center text-white bg-transparent border-0 mx-auto"
              style={{ opacity: canBack ? 0.95 : 0.4 }}
              aria-label="delete"
              data-testid="lockscreen-key-back"
            >
              <svg width="34" height="24" viewBox="0 0 34 24" fill="none" aria-hidden="true">
                <path
                  d="M11 2L2 12l9 10h17a4 4 0 0 0 4-4V6a4 4 0 0 0-4-4H11Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 8l8 8M24 8l-8 8"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          );
        }
        return (
          <button
            key={i}
            type="button"
            onClick={() => onDigit(d)}
            className="w-[76px] h-[76px] rounded-full flex flex-col items-center justify-center text-white bg-white/[0.18] active:bg-white/30 transition-colors border-0 mx-auto"
            style={{
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
            data-testid={`lockscreen-key-${d}`}
          >
            <span className="text-[34px] leading-none font-light tabular-nums">
              {d}
            </span>
            <span className="text-[10px] tracking-[0.18em] font-semibold mt-0.5 opacity-95">
              {LETTERS[d] || ""}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function QuickAction({ kind, onTap }) {
  const interactive = typeof onTap === "function";
  const handleClick = interactive
    ? (e) => {
        // Stop propagation so this tap never leaks into the wallpaper
        // swipe-tracker on the parent (which would spuriously start a
        // drag). Preventing default too — no browser tooltip on iOS.
        e.stopPropagation();
        e.preventDefault();
        onTap();
      }
    : undefined;
  return (
    <div
      role={interactive ? "button" : undefined}
      onClick={handleClick}
      onPointerDown={
        interactive
          ? (e) => {
              // Prevent the parent's swipe-tracker from claiming this
              // pointer. Without this, the tap would register as a
              // 0-px "drag start" and the idle layer would jitter.
              e.stopPropagation();
            }
          : undefined
      }
      className={`w-[46px] h-[46px] rounded-full flex items-center justify-center ${
        interactive
          ? "pointer-events-auto cursor-pointer active:scale-95 transition-transform"
          : ""
      }`}
      style={{
        // iOS 17/18 lockscreen quick-action: light "glass" disc that
        // picks up the wallpaper tint through the backdrop-filter blur.
        // A very faint white overlay + saturation boost gives the same
        // frosted look you see on the real Apple lockscreen.
        background: "rgba(255,255,255,0.14)",
        backdropFilter: "blur(18px) saturate(140%)",
        WebkitBackdropFilter: "blur(18px) saturate(140%)",
        boxShadow: "inset 0 0 0 0.5px rgba(255,255,255,0.14)",
      }}
      data-testid={`lockscreen-quick-action-${kind}`}
      aria-hidden={interactive ? undefined : "true"}
    >
      {kind === "flashlight" ? (
        // Filled iOS flashlight silhouette (SF Symbols "flashlight.on.fill"-style).
        // Trapezoidal head → thin neck band → rounded body with a subtle
        // button notch. All solid white — no strokes — matching real iOS.
        <svg width="18" height="20" viewBox="0 0 18 20" aria-hidden="true">
          <path
            d="M4.4 0.8 h9.2 a0.6 0.6 0 0 1 0.6 0.6 v1.9 a0.6 0.6 0 0 1 -0.6 0.6 h-9.2 a0.6 0.6 0 0 1 -0.6 -0.6 v-1.9 a0.6 0.6 0 0 1 0.6 -0.6 z
               M5.1 4.7 h7.8 l-0.35 1.75 a0.6 0.6 0 0 1 -0.59 0.5 h-5.92 a0.6 0.6 0 0 1 -0.59 -0.5 z
               M6.35 7.55 h5.3 a0.7 0.7 0 0 1 0.7 0.7 v10.15 a0.6 0.6 0 0 1 -0.6 0.6 h-5.5 a0.6 0.6 0 0 1 -0.6 -0.6 v-10.15 a0.7 0.7 0 0 1 0.7 -0.7 z"
            fill="white"
          />
          <rect x="7.9" y="10.1" width="2.2" height="0.9" rx="0.35" fill="rgba(0,0,0,0.28)" />
        </svg>
      ) : (
        // Filled iOS camera silhouette (SF Symbols "camera.fill"-style).
        // Rounded body + small viewfinder bump on top + concentric lens.
        // The lens rim is a slightly darker inner ring so the "hole" reads
        // clearly against the white body.
        <svg width="22" height="18" viewBox="0 0 22 18" aria-hidden="true">
          <path
            d="M7.4 2.4 l0.9 -1.6 A1 1 0 0 1 9.15 0.3 h3.7 a1 1 0 0 1 0.86 0.5 l0.9 1.6 h4.15 a2.5 2.5 0 0 1 2.5 2.5 v10.1 a2.5 2.5 0 0 1 -2.5 2.5 h-16.3 a2.5 2.5 0 0 1 -2.5 -2.5 v-10.1 a2.5 2.5 0 0 1 2.5 -2.5 z"
            fill="white"
          />
          {/* Lens: outer rim slightly darker → inner white → tiny dark pupil for depth */}
          <circle cx="11" cy="10" r="3.55" fill="rgba(0,0,0,0.35)" />
          <circle cx="11" cy="10" r="2.85" fill="white" />
          <circle cx="11" cy="10" r="1.95" fill="rgba(0,0,0,0.22)" />
          {/* Flash dot top-right */}
          <circle cx="17.6" cy="4.9" r="0.55" fill="rgba(0,0,0,0.28)" />
        </svg>
      )}
    </div>
  );
}
